/**
 * 統一プロンプトテンプレート 新規作成 / 編集ダイアログ。
 */
import { useEffect, useState } from 'react';

import type {
  PromptScope,
  PromptTemplate,
  PromptTemplateCategory,
  PromptTemplateField,
} from '@goenhub/shared';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import {
  useCreatePromptTemplate,
  useUpdatePromptTemplate,
  type PromptTemplateRecord,
} from '@/hooks/usePromptData';

import { FieldEditor } from './FieldEditor';

interface TemplateFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: PromptTemplateRecord;
  isDuplicate?: boolean;
}

interface FormState {
  name: string;
  scope: PromptScope;
  category: PromptTemplateCategory;
  description: string;
  fields: PromptTemplateField[];
  isDefault: boolean;
}

const EMPTY: FormState = {
  name: '',
  scope: 'both',
  category: 'BtoB',
  description: '',
  fields: [],
  isDefault: false,
};

export function TemplateFormDialog({
  open,
  onOpenChange,
  initial,
  isDuplicate = false,
}: TemplateFormDialogProps) {
  const createMutation = useCreatePromptTemplate();
  const updateMutation = useUpdatePromptTemplate();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const isEdit = !!initial && !isDuplicate;

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setForm({
        name: isDuplicate ? `${initial.name} (コピー)` : initial.name,
        scope: initial.scope,
        category: initial.category,
        description: initial.description ?? '',
        fields: (initial.fields ?? []).map((f) => ({ ...f })),
        isDefault: isDuplicate ? false : initial.isDefault ?? false,
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, initial, isDuplicate]);

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      toast.error('テンプレート名は必須です');
      return;
    }
    if (form.fields.some((f) => !f.label.trim())) {
      toast.error('すべてのフィールドにラベルを入力してください');
      return;
    }

    setSubmitting(true);
    try {
      const payload: Partial<PromptTemplate> = {
        name: form.name.trim(),
        scope: form.scope,
        category: form.category,
        description: form.description.trim(),
        fields: form.fields,
        isDefault: form.isDefault,
      };

      if (isEdit && initial) {
        await updateMutation.mutateAsync({ id: initial.id, data: payload });
        toast.success('テンプレートを更新しました');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success(isDuplicate ? 'テンプレートを複製しました' : 'テンプレートを作成しました');
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(`保存に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'テンプレート編集' : isDuplicate ? 'テンプレート複製' : '新規テンプレート作成'}
          </DialogTitle>
          <DialogDescription>
            メール生成 / LINE NEWS 生成で共通利用するプロンプト雛形を編集します。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="tpl-name">テンプレート名</Label>
              <Input
                id="tpl-name"
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                placeholder="例: セミナー告知"
                autoFocus
              />
            </div>
            <div>
              <Label>スコープ</Label>
              <Select
                value={form.scope}
                onValueChange={(v) => setForm((s) => ({ ...s, scope: v as PromptScope }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mail">メール専用</SelectItem>
                  <SelectItem value="line_news">LINE NEWS 専用</SelectItem>
                  <SelectItem value="both">両方で利用可</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>カテゴリ</Label>
              <Select
                value={form.category}
                onValueChange={(v) =>
                  setForm((s) => ({ ...s, category: v as PromptTemplateCategory }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="BtoB">BtoB</SelectItem>
                  <SelectItem value="BtoC">BtoC</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                  <SelectItem value="announcement">announcement</SelectItem>
                  <SelectItem value="campaign">campaign</SelectItem>
                  <SelectItem value="tips">tips</SelectItem>
                  <SelectItem value="other">other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="tpl-description">説明</Label>
            <Textarea
              id="tpl-description"
              value={form.description}
              onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
              placeholder="このテンプレートの用途・想定シチュエーション"
              rows={2}
            />
          </div>

          <div className="flex items-start gap-2 p-3 rounded border border-[var(--border)] bg-[var(--bg-elevated)]">
            <input
              id="tpl-isDefault"
              type="checkbox"
              checked={form.isDefault}
              onChange={(e) => setForm((s) => ({ ...s, isDefault: e.target.checked }))}
              className="size-4 mt-0.5"
            />
            <div>
              <Label htmlFor="tpl-isDefault" className="mb-0 font-medium">
                デフォルトテンプレートとして保護
              </Label>
              <p className="text-xs text-[var(--text-muted)] mt-0.5">
                ON にすると、このテンプレートは削除できなくなります（標準提供として保護）
              </p>
            </div>
          </div>

          <FieldEditor
            fields={form.fields}
            onChange={(fields) => setForm((s) => ({ ...s, fields }))}
          />
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            キャンセル
          </Button>
          <Button type="button" variant="primary" onClick={handleSubmit} disabled={submitting}>
            {submitting ? '保存中...' : isEdit ? '更新' : '作成'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
